import { PriceUtils } from '@wix/bookings-uou-mappers';
import { useTranslation } from '@wix/yoshi-flow-editor';
import React from 'react';
import { Divider } from 'wix-ui-tpa/cssVars';
import { ServicePaymentDetails } from '../../../../../types/types';
import { TagName } from '../../../../../utils/tagName.const';
import Text, { TextType } from '../../../Text/Text';
import { PaymentSummaryDataHook } from '../dataHooks';
import { classes as parentClasses } from '../PaymentSummary.st.css';
import { classes } from './SubscriptionSummary.st.css';
import { PriceItem } from '../PriceItem/PriceItem';

type SubscriptionSummaryProps = {
  price: number;
  currency: string;
  locale: string;
  paymentDetails: ServicePaymentDetails;
  numberOfParticipants: number;
  isCart: boolean | undefined;
};

export const SubscriptionSummary: React.FC<SubscriptionSummaryProps> = ({
  price,
  currency,
  locale,
  paymentDetails,
  numberOfParticipants,
  isCart,
}) => {
  const { t } = useTranslation();

  const baseEnrollmentFee = paymentDetails.subscriptionInfo
    ?.enrollmentFeeAmount
    ? Number(paymentDetails.subscriptionInfo.enrollmentFeeAmount.value)
    : 0;
  const enrollmentFeeValue = baseEnrollmentFee * numberOfParticipants;

  const info = paymentDetails.subscriptionInfo;
  const pricePerParticipant = price;
  const totalPrice = price * numberOfParticipants;
  const formattedPricePerParticipant = PriceUtils.getFormattedCurrency({
    price: pricePerParticipant,
    currency,
    locale,
  });

  return (
    <div className={parentClasses.singleSession}>
      <div className={parentClasses.details}>
        <div className={classes.subscriptionSubtotalGroup}>
          <PriceItem
            type={TextType.Primary}
            labels={[t('app.payment.summary.subtotal.text')]}
            price={t('app.payment.summary.subscription.monthly-price', {
              price: PriceUtils.getFormattedCurrency({
                price: totalPrice,
                currency,
                locale,
              }),
            })}
            ariaPrice={PriceUtils.getFormattedCurrency({
              price: totalPrice,
              currency,
              locale,
              currencyDisplay: 'name',
            })}
            dataHook={PaymentSummaryDataHook.Subscription_Price}
            isCart={isCart}
          />
          <div className={classes.subscriptionInfo}>
            {numberOfParticipants > 1 && (
              <Text type={TextType.Secondary} tagName={TagName.Paragraph}>
                {t('app.payment.summary.subscription.participants', {
                  count: numberOfParticipants,
                  price: t(
                    'app.payment.summary.subscription.monthly-price',
                    { price: formattedPricePerParticipant },
                  ),
                })}
              </Text>
            )}
            {info && (
              <Text type={TextType.Secondary} tagName={TagName.Paragraph}>
                {t('app.payment.summary.subscription.payment-number', {
                  current: 1,
                  total: info.totalPayments,
                })}
              </Text>
            )}
          </div>
        </div>
        {paymentDetails.subscriptionInfo?.enrollmentFeeAmount && (
          <div className={classes.enrollmentFee}>
            <PriceItem
              type={TextType.Primary}
              labels={[t('app.payment.summary.enrollment-fee.label')]}
              price={PriceUtils.getFormattedCurrency({
                price: enrollmentFeeValue,
                currency,
                locale,
              })}
              ariaPrice={PriceUtils.getFormattedCurrency({
                price: enrollmentFeeValue,
                currency,
                locale,
                currencyDisplay: 'name',
              })}
              dataHook={PaymentSummaryDataHook.Enrollment_Fee}
              isCart={isCart}
            />
          </div>
        )}
        <Divider className={parentClasses.divider} />
        <PriceItem
          type={TextType.Primary}
          labels={[t('app.payment.summary.total-due-now.label')]}
          price={PriceUtils.getFormattedCurrency({
            price: totalPrice + enrollmentFeeValue,
            currency,
            locale,
          })}
          ariaPrice={PriceUtils.getFormattedCurrency({
            price: totalPrice + enrollmentFeeValue,
            currency,
            locale,
            currencyDisplay: 'name',
          })}
          dataHook={PaymentSummaryDataHook.Pay_Now}
          isCart={isCart}
        />
      </div>
    </div>
  );
};
